/**
 * Interface for representing an option in the select dropdown.
 */
export interface IOption {
  /**
   * The label to display for the option in the dropdown.
   */
  label: string;

  /**
   * The value associated with the option, used for selection and identification.
   */
  value: string | number;
}

export interface ISelect {
  /**
   * The currently selected option. If null, no option is selected.
   */
  selectedOption: IOption | null;

  /**
   * Array of options to render in the dropdown.
   */
  options: IOption[];

  /**
   * Placeholder text displayed when no option is selected.
   */
  placeholder?: string;

  /**
   * Whether to show a clear button to remove the selected option.
   */
  showClear?: boolean;

  /**
   * Whether the dropdown should support searching for options.
   */
  searchable?: boolean;

  /**
   * Placeholder text displayed in the search input field.
   */
  filterPlaceholder?: string;

  /**
   * Optional label displayed above the select component.
   */
  label?: string;

  /**
   * Whether the field is required for form submission.
   */
  required?: boolean;

  /**
   * Whether the dropdown is disabled and not selectable.
   */
  disabled?: boolean;

  /**
   * Whether the selection has changed. Used for styling purposes.
   */
  hasChanged?: boolean;

  /**
   * The style variant to apply when the selection has changed.
   */
  selectChangedStyle?: SelectChangeVariant;

  /**
   * Whether to highlight the selection when it has changed.
   */
  highlightOnChange?: boolean;

  /**
   * Whether to highlight the border of the select component.
   */
  highlightBorder?: boolean;

  /**
   * Optional icon to display alongside the select field.
   */
  icon?: string;

  /**
   * Message displayed when no options are available in the dropdown.
   */
  emptyMessage?: string;

  /**
   * Default text to prefill in the search input, useful for server-side filtering.
   */
  defaultSearchedText?: string;

  /**
   * Whether the dropdown is in a loading state, typically when fetching options.
   */
  loading?: boolean;

  /**
   * Additional class name to apply to the select component.
   */
  className?: string;

  /**
   * Additional class name to apply to the dropdown wrapper.
   */
  dropdownWrapperClassName?: string;

  /**
   * Callback triggered when an option is selected or cleared.
   * @param option - The newly selected option or null if cleared.
   */
  onChange: (option: IOption | null) => void;

  /**
   * Callback triggered when the search text changes.
   * @param value - The updated search text value.
   */
  onSearchTextChange?: (value: string) => void;

  /**
   * Optional DOM element or selector where the dropdown will be appended.
   * Useful for handling overflow and z-index issues.
   */
  appendTo?: HTMLElement | 'self' | (() => HTMLElement) | null | undefined;
}

export enum SelectChangeVariant {
  Default = '',
  Secondary = 'secondary',
}
